import { useState, useEffect } from "react";
import { NavLink, Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ShieldAlert,
  Network,
  Sparkles,
  Settings,
  Wand2,
  Target,
  AlertTriangle,
  Truck,
  ListChecks,
  Bell,
  Filter,
  ArrowUpRight,
  Crown,
  Compass,
  Coins,
  Users,
  ClipboardCheck,
  Award,
  FolderOpen,
  GitCompare,
  Radio,
  TrendingUp,
  Inbox,
  FileSearch,
  BookOpen,
  Activity,
  Anchor,
  ChevronDown,
  MessageSquare,
  MessagesSquare,
  Handshake,
  GitBranch,
  Building2,
  Briefcase,
  Search,
  Menu,
  X,
  MoreHorizontal,
  Download,
  Library,
  Calendar,
  Clock,
  UploadCloud,
  ClipboardList,
  Globe,
  Sliders,
  Sun,
  HelpCircle,
  Hexagon,
  Gauge,
  KanbanSquare,
  Scale3d,
  Wrench,
} from "lucide-react";
import { api } from "@/lib/api";
import CommandPalette from "@/components/CommandPalette";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import UserMenu from "@/components/UserMenu";
import DemoUserMenu from "@/components/DemoUserMenu";
import ContextSyncIndicator from "@/components/ContextSyncIndicator";
import { AsyncTasksBadge } from "@/hooks/useAsyncTasks";
import WelcomeModal from "@/components/WelcomeModal";
import DemoBanner from "@/components/DemoBanner";
import OrgSwitcher from "@/components/OrgSwitcher";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import PeriodPicker from "@/components/PeriodPicker";
import ImportIntelligentModal from "@/components/ImportIntelligentModal";
import BriefComiteModalRoot from "@/components/BriefComiteModal";
import ExternalLensModal from "@/components/ExternalLensModal";
import OnboardingFlow from "@/components/OnboardingFlow";
import PersonaSwitcher from "@/components/PersonaSwitcher";
import { useDemoMode } from "@/lib/demoMode";
import ContextButton from "@/components/ContextButton";
import LlmQuotaBadge from "@/components/LlmQuotaBadge";
import { useEdgeSwipe } from "@/lib/useEdgeSwipe";
import ViewSwitcher from "@/components/ViewSwitcher";
import SystemSelector from "@/components/SystemSelector";
import SystemSidebar from "@/components/SystemSidebar";
import { SYSTEM_DEFS, systemForPath, useSystems } from "@/lib/systems";
import { useActiveView } from "@/lib/useActiveView";
import { Layers as LayersIcon } from "lucide-react";
import { Brain } from "lucide-react";
import { MetacogUnreadProvider, useMetacogUnread, MetacognitionLoginPopup } from "@/components/MetacognitionAlert";
import { usePersona } from "@/lib/persona";
import IntroductionBanner from "@/components/IntroductionBanner";
import AgenticGlobalBar from "@/components/AgenticGlobalBar";

// Map URL path → module key for the global Contexte button (additive).
// Modules already embedding their own per-page <ContextButton/> are NOT listed
// here, so users won't see a duplicate. New modules with no per-page button
// inherit one for free via this map.
const MODULE_KEY_BY_PATH = {
  "/app/immobilier": "immobilier",
  "/app/courtage": "courtage",
  "/app/deals": "deals",
  "/app/negociation": "negociation",
  "/app/chainage": "chainage",
  "/app/problem-gravity": "problem-gravity",
  "/app/utility-mapping": "utility-mapping",
  "/app/operational-gravity": "operational-gravity",
  "/app/bouteille-a-la-mer": "cercle",
  "/app/cercle": "cercle",
  "/app/chat-interne": "chat_interne",
  "/app/trend": "trend",
  "/app/feed": "trend",
  "/app/executive": "executive",
  "/app/dashboard": "dashboard",
  "/app/doctrine": "doctrine",
  "/app/leads": "leads",
  "/app/intelligence": "intelligence",
  "/app/ontologie": "ontologie",
  "/app/analyses": "analyses",
  "/app/annuaire": "annuaire",
  "/app/graph": "annuaire",
  "/app/documents": "documents",
  "/app/asymetries": "asymetries",
  "/app/radar": "radar",
  "/app/brvm": "brvm",
  "/app/briefs-library": "briefs_library",
  "/app/digests": "digests",
};

function moduleKeyForPath(pathname) {
  if (!pathname) return null;
  // Normalize the /demo prefix to /app for lookup
  const normalized = pathname.replace(/^\/demo/, "/app");
  // Direct match first
  if (MODULE_KEY_BY_PATH[normalized]) return MODULE_KEY_BY_PATH[normalized];
  // Match longest prefix (handles /app/risques/:id etc — already covered by per-page btn)
  for (const key of Object.keys(MODULE_KEY_BY_PATH)) {
    if (normalized.startsWith(key + "/")) return MODULE_KEY_BY_PATH[key];
  }
  return null;
}
import { useTranslation } from "react-i18next";
import { getUniverses } from "@/lib/universes";

// Taxonomie UNIQUE (doctrine 2026-09-09) : voir src/lib/universes.js —
// consommée aussi par la couche instrumentale agentique. Zéro duplication.
// Les univers agenticOnly (Instruments) restent hors sidebar Expert (A2).
function getSections(t, isDemo) {
  return getUniverses(t, isDemo).filter((u) => !u.agenticOnly);
}

export default function SaaSLayout() {
  const { t } = useTranslation();
  const isDemo = useDemoMode();
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    try {
      const p = location.pathname;
      if (/^\/app\/(hub|deal-system|deal-pipeline|deals|decisions|brvm|gouvernance)/.test(p)) {
        localStorage.setItem("noam_landing", p.startsWith("/app/deal") ? "/app/deal-system" : p.split("/").slice(0, 3).join("/"));
      }
    } catch {}
  }, [location.pathname]);
  const { labelFor: personaLabelFor, manifest: personaManifest } = usePersona();
  const globalContextModuleKey = !isDemo ? moduleKeyForPath(location.pathname) : null;
  const basePath = isDemo ? "/demo" : "/app";
  const remap = (to) => to.replace(/^\/app/, basePath);
  const [meData, setMeData] = useState(null);
  const allowedModules = isDemo ? null : (meData?.modules || null);
  // AGENTIC V1 — mode d'expérience : agentic (défaut nouveaux comptes) | expert.
  // Le mode Expert est une PERMISSION SERVEUR (fail closed), jamais un code client.
  const [expMode, setExpMode] = useState(null);
  useEffect(() => {
    if (isDemo) return;
    api.get("/workspace/agentic/mode").then((r) => setExpMode(r.data)).catch(() => {});
  }, [isDemo]);
  const agenticMode = !isDemo && expMode?.mode === "agentic";
  const switchExperience = async (mode) => {
    try {
      let code;
      if (mode === "expert") {
        code = window.prompt("Code d'accès au mode Expert :");
        if (!code) return;
      }
      await api.post("/workspace/agentic/mode", { mode, code });
      const r = await api.get("/workspace/agentic/mode");
      setExpMode(r.data);
      window.location.href = mode === "agentic" ? "/app/noam" : "/app/hub";
    } catch (e) {
      const msg = e?.response?.data?.detail || "Changement de mode refusé.";
      window.alert(msg);
    }
  };
  // D3 — contexte System dérivé de l'URL ; affichage seulement, jamais une
  // permission (le gating serveur D1 reste la seule sécurité)
  const { systems: enabledSystems, depth: systemsDepth } = useSystems();
  const activeSystem = !isDemo ? systemForPath(location.pathname, location.search) : null;
  const systemNavActive = !!(activeSystem && enabledSystems.includes(activeSystem));
  const myRole = meData?.user?.role;
  const isUltraAdmin = !!meData?.user?.is_ultra_admin;
  const canManageUsers = !isDemo && (myRole === "admin" || myRole === "super_admin" || isUltraAdmin);
  // V20.1 P0 fix — Filtre Smart Cockpit : consomme la vue active pour cacher/grouper
  // les modules selon la vue choisie (Standard / Consolidée / Comité direction / vue perso).
  // Pour "Standard" (tous modules visibles), `viewModuleSet=null` → comportement d'origine.
  // Pour les autres vues, on garde uniquement les modules listés dans la vue.
  // Les groupes (g-*) sont injectés en début de section "Stratégique" comme raccourcis hub.
  const { view: activeView } = useActiveView();
  const viewIsStandard = !activeView || activeView.name === "Standard";
  // V23.15.30 — Quand un groupe est actif dans la vue consolidée, ses enfants
  // ne doivent PAS apparaître comme items individuels de sidebar : le hub
  // groupé (injecté dans viewGroupHubs) est le seul point d'accès. Sinon les
  // modules apparaissent deux fois (une fois en hub, une fois en item plat).
  const GROUP_CHILDREN_KEYS = {
    "g-gravites": ["operational_gravity", "problem_gravity"],
    "g-risque-conformite": ["risques", "conformite", "audits", "controles", "incidents", "alertes"],
    "g-capital": ["financement", "origination_capital", "sources_capital"],
    "g-cartographies": ["chainage", "utility_mapping", "ontologie", "cartographies", "graphe_noeuds"],
    "g-origination": ["origination", "negociation", "asymetries", "positionnement"],
  };
  const activeGroupChildren = new Set();
  if (!isDemo && !viewIsStandard && activeView?.modules) {
    for (const m of activeView.modules) {
      if (m.is_group && m.visible !== false && GROUP_CHILDREN_KEYS[m.key]) {
        for (const childKey of GROUP_CHILDREN_KEYS[m.key]) {
          activeGroupChildren.add(childKey);
        }
        // Aussi retirer les enfants déclarés dans la vue elle-même
        for (const c of (m.children || [])) {
          if (c.key) activeGroupChildren.add(c.key);
        }
      }
    }
  }
  const viewModuleSet = (() => {
    if (isDemo || viewIsStandard || !activeView?.modules) return null;
    const keys = new Set();
    for (const m of activeView.modules) {
      if (m.visible === false) continue;
      if (m.is_group) {
        // V23.15.30 — SKIP : les enfants du groupe passent par le hub, pas en sidebar plate
        continue;
      }
      // Si ce module est enfant d'un groupe actif, on skip aussi (dédup)
      if (activeGroupChildren.has(m.key)) continue;
      keys.add(m.key);
    }
    return keys;
  })();
  // Modules explicitement désactivés (visible:false) par la vue active — distinct
  // de l'omission silencieuse (doctrine Radar).
  const viewExplicitlyHidden = (() => {
    if (isDemo || viewIsStandard || !activeView?.modules) return new Set();
    return new Set(activeView.modules.filter((m) => m.visible === false).map((m) => m.key));
  })();
  const viewGroupHubs = (() => {
    if (isDemo || viewIsStandard || !activeView?.modules) return [];
    const slugMap = {
      "g-gravites": { slug: "gravites", label: "Gravités" },
      "g-risque-conformite": { slug: "risque-conformite", label: "Risque & Conformité" },
      "g-capital": { slug: "capital", label: "Capital" },
      "g-cartographies": { slug: "cartographies", label: "Cartographies" },
      "g-origination": { slug: "origination-hub", label: "Origination Hub" },
    };
    return activeView.modules
      .filter((m) => m.is_group && m.visible !== false && slugMap[m.key])
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((m) => ({
        to: remap(`/app/${slugMap[m.key].slug}`),
        label: m.label || slugMap[m.key].label,
        icon: LayersIcon,
        isGroupHub: true,
      }));
  })();
  const SECTIONS_ADMIN_IDX = 6;
  const SECTIONS_RAW = getSections(t, isDemo).map((s) => ({
    ...s,
    items: s.items
      .filter((it) => !allowedModules || !it.module || allowedModules.includes(it.module))
      // W17-B5 — Complexity Policy : le profil (ui_level) ORDONNE l'exposition,
      // jamais les droits. never_hide (alertes, plans) reste toujours visible.
      .filter((it) => {
        if (isDemo || !it.module || !personaManifest?.whitelist_active) return true;
        if ((personaManifest.never_hide || []).includes(it.module)) return true;
        // Doctrine : le Radar ne disparaît jamais silencieusement (persona = exposition, pas droits)
        if (it.module === "radar") return true;
        return (personaManifest.visible_modules || []).includes(it.module);
      })
      // W17 — la sidebar globale respecte systems_enabled + system_depth :
      // un item appartenant à un System désactivé (ou au-delà de la profondeur)
      // n'apparaît plus (le gating serveur reste la seule sécurité).
      .filter((it) => {
        if (isDemo) return true;
        const sys = systemForPath(it.to || "");
        if (!sys) return true;
        if (!enabledSystems.includes(sys)) return false;
        const [itPath] = (it.to || "").split("?");
        const def = SYSTEM_DEFS.find((d) => d.id === sys);
        const navIt = def?.nav.find((n) => n.to.split("?")[0] === itPath);
        if (navIt?.min_depth == null) return true;
        return navIt.min_depth <= (systemsDepth?.[sys] ?? 99);
      })
      // V20.1 P0 — filtre Vues intelligentes : si vue active != Standard, ne garder
      // que les modules présents dans la vue (matching par `it.module` ou path).
      .filter((it) => {
        if (!viewModuleSet) return true;
        if (it.module && viewModuleSet.has(it.module)) return true;
        // Doctrine Radar : masqué UNIQUEMENT si la vue le désactive explicitement
        // (visible:false) — jamais par simple omission silencieuse.
        if (it.module === "radar" && !viewExplicitlyHidden.has("radar")) return true;
        // fallback : déduire la key depuis le path (utile si it.module non renseigné)
        const pathKey = (it.to || "").replace(/^\/(app|demo)\//, "").split("/")[0];
        return viewModuleSet.has(pathKey);
      })
      .map((it) => ({ ...it, to: remap(it.to) })),
  }))
    // Append Users link to "Travail quotidien" for org admins
    .map((s, idx) => {
      if (idx === SECTIONS_ADMIN_IDX && canManageUsers) {
        return { ...s, items: [...s.items,
          { to: "/app/users", label: t("saas.nav.users", { defaultValue: "Utilisateurs" }), icon: Users },
          { to: "/app/facturation", label: "Facturation", icon: Coins },
        ] };
      }
      return s;
    })
    .filter((s) => s.items.length > 0);
  // V20.1 P0 — injecter les hubs de groupes en tête de la 1ère section (Stratégique)
  // lorsqu'une vue consolidée est active (ex: Origination Hub, Cartographies…).
  if (viewGroupHubs.length > 0 && SECTIONS_RAW.length > 0) {
    SECTIONS_RAW[0] = {
      ...SECTIONS_RAW[0],
      items: [...viewGroupHubs, ...SECTIONS_RAW[0].items],
    };
  }
  // A1.2 — le hub « Cartographies » vit désormais dans « Outils avancés »
  // (compression : plus d'injection permanente en tête de navigation).
  // Gouvernance : la Métacognition n'apparaît QUE dans la sidebar Ultra-Admin
  // (/ultra-admin → lien Métacognition). Retirée de la navigation workspace.
  const SECTIONS = SECTIONS_RAW;
  const [collapsed, setCollapsed] = useState(false);
  // A2 — accordéon 6 univers : un seul ouvert, auto-ouvert selon la route
  const [openUniverse, setOpenUniverse] = useState(null);
  useEffect(() => {
    const path = location.pathname;
    const sec = SECTIONS.find((s) => s.items.some((it) => path.startsWith((it.to || "").split("?")[0])));
    if (sec) setOpenUniverse(sec.label);
    else if (openUniverse === null && SECTIONS[0]) setOpenUniverse(SECTIONS[0].label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  // A1.2 — « Outils avancés » replié par défaut (persisté)
  const [advancedOpen, setAdvancedOpen] = useState(() => {
    try { return localStorage.getItem("noam_nav_advanced") === "1"; } catch { return false; }
  });
  const toggleAdvanced = () => setAdvancedOpen((v) => {
    try { localStorage.setItem("noam_nav_advanced", v ? "0" : "1"); } catch {}
    return !v;
  });
  const [phase, setPhase] = useState(isDemo ? "Stabilisation" : "");
  const [phaseOpen, setPhaseOpen] = useState(false);
  const [phaseAnchor, setPhaseAnchor] = useState({ top: 56, left: 16 });
  const PHASES = ["Stabilisation", "Déploiement", "Restructuration"];
  const [period, setPeriod] = useState("T1 2026");
  const [periods, setPeriods] = useState(["T1 2026", "T4 2025", "T3 2025", "T2 2025"]);
  // Period dropdown is now handled by the standalone <PeriodPicker /> component.
  // The states above are kept only to feed legacy callers if any; the new picker
  // reads/writes via localStorage + the "noam:period-changed" event.
  const [mobileOpen, setMobileOpen] = useState(false);
  // P2 V13 — swipe gestures on mobile (open from left edge / close by swiping left)
  useEdgeSwipe({
    enabled: true,
    edgeZone: 28,
    onOpenSwipe: () => {
      if (typeof window !== "undefined" && window.innerWidth < 1024) setMobileOpen(true);
    },
    onCloseSwipe: () => setMobileOpen(false),
  });
  const [orgName, setOrgName] = useState(null);
  const [orgId, setOrgId] = useState(null);
  // P1 chantier 2 — Loupe externe
  const [lensOpen, setLensOpen] = useState(false);
  // V23.14.10 — Modals déclenchées depuis le menu Actions (état au niveau layout
  // pour qu'elles ne se démontent pas quand le menu se ferme)
  const [importOpen, setImportOpen] = useState(false);
  const [briefOpen, setBriefOpen] = useState(false);
  // Topbar overflow menu (regroupe les actions secondaires pour éviter l'encombrement)
  const [moreOpen, setMoreOpen] = useState(false);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [moreAnchor, setMoreAnchor] = useState({ top: 56, right: 16 });
  useEffect(() => {
    if (!moreOpen) return;
    const onClick = (e) => {
      if (!e.target.closest?.("[data-topbar-more]") && !e.target.closest?.("[data-topbar-more-menu]")) {
        setMoreOpen(false);
      }
    };
    const onScroll = () => setMoreOpen(false);
    window.addEventListener("click", onClick);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("click", onClick);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [moreOpen]);
  // Bumped each time the user picks a new period — used as <Outlet/> key so the
  // active page remounts and all its loaders re-run with the new ?period filter.
  const [periodEpoch, setPeriodEpoch] = useState(0);

  useEffect(() => {
    if (!phaseOpen) return;
    const onClick = (e) => {
      if (!e.target.closest?.("[data-topbar-phase]") && !e.target.closest?.("[data-topbar-phase-menu]")) {
        setPhaseOpen(false);
      }
    };
    const onScroll = () => setPhaseOpen(false);
    window.addEventListener("click", onClick);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("click", onClick);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [phaseOpen]);

  useEffect(() => {
    const onPeriodChanged = () => setPeriodEpoch((x) => x + 1);
    window.addEventListener("noam:period-changed", onPeriodChanged);
    return () => window.removeEventListener("noam:period-changed", onPeriodChanged);
  }, []);

  // V23.15.28 — Badge "IA" : point discret si le brief pro-actif de Noam n'a
  // pas encore été vu par l'utilisateur. Poll léger toutes les 3 min tant que
  // l'onglet est visible. Zéro cost cognitif : juste un dot vert sur la nav IA.
  const [aiBriefUnseen, setAiBriefUnseen] = useState(false);
  // V23.16 — Badge Chat interne : non-lus + @mentions (poll léger 45 s).
  const [chatUnread, setChatUnread] = useState({ total: 0, mentions: 0 });
  useEffect(() => {
    if (isDemo) return;
    let cancelled = false;
    const check = async () => {
      try {
        const r = await api.get("/workspace/chat-interne/unread-summary");
        if (!cancelled) setChatUnread({ total: r.data?.total || 0, mentions: r.data?.mentions || 0 });
      } catch { /* silencieux */ }
    };
    check();
    const iv = setInterval(check, 45 * 1000);
    const onVis = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { cancelled = true; clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  }, [isDemo, orgId, location.pathname]);
  useEffect(() => {
    if (isDemo) return; // Pas de brief pro-actif en mode démo (contenu figé)
    let cancelled = false;
    const check = async () => {
      try {
        const r = await api.get("/workspace/intelligence/v2/proactive-brief/status");
        if (!cancelled) setAiBriefUnseen(!!r.data?.unseen);
      } catch {
        /* silencieux */
      }
    };
    check();
    const iv = setInterval(check, 3 * 60 * 1000);
    // Refresh quand l'utilisateur revient sur l'onglet
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [isDemo, orgId]);

  useEffect(() => {
    if (isDemo) {
      api.get("/executive/phase").then((r) => setPhase(r.data.current)).catch(() => {});
      api.get("/executive/periods").then((r) => { setPeriod(r.data.current); setPeriods(r.data.available); }).catch(() => {});
    } else {
      // Authenticated workspace — fetch real org name + phase + plan/modules
      const loadOrg = () => {
        api.get("/workspace/organisation").then((r) => {
          setOrgName(r.data?.name || "");
          setOrgId(r.data?.id || null);
          setPhase(r.data?.phase || "Stabilisation");
        }).catch(() => setOrgName(""));
        api.get("/workspace/me").then((r) => setMeData(r.data)).catch(() => setMeData(null));
      };
      loadOrg();
      window.addEventListener("noam:org-updated", loadOrg);
      // V23.16.7 — Recharger /workspace/me quand les modules changent (toggle Settings)
      window.addEventListener("noam:modules-updated", loadOrg);
      api.get("/executive/periods").then((r) => { setPeriod(r.data.current); setPeriods(r.data.available); }).catch(() => {});
      return () => {
        window.removeEventListener("noam:org-updated", loadOrg);
        window.removeEventListener("noam:modules-updated", loadOrg);
      };
    }
  }, [isDemo]);

  const onOrgChanged = () => {
    // Trigger a fresh full-app reload so all data refetches against the new active org
    window.location.reload();
  };

  const changePeriod = async (p) => {
    setPeriod(p);
    try { await api.post("/executive/period", { period: p }); }
    catch { /* silent — defensive */ }
  };

  const changePhase = async (p) => {
    setPhase(p); setPhaseOpen(false);
    if (isDemo) {
      try { await api.post("/executive/phase", { phase: p }); }
      catch { /* silent — defensive */ }
    } else {
      try {
        await api.patch("/workspace/organisation", { phase: p });
        window.dispatchEvent(new CustomEvent("noam:org-updated"));
      } catch { /* silent — defensive */ }
    }
  };

  const sidebarWidth = collapsed ? "w-16" : "w-64";

  return (
    <MetacogUnreadProvider isUltraAdmin={isUltraAdmin}>
    <div className="min-h-screen flex bg-[var(--noam-bg)] text-white">
      <CommandPalette />
      {isUltraAdmin && <MetacognitionLoginPopup basePath={basePath} />}

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/60 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      <aside
        className={`${sidebarWidth} shrink-0 border-r border-white/5 h-screen overflow-y-auto transition-all duration-300 z-50 ${
          mobileOpen ? "fixed top-0 left-0 block" : "sticky top-0 hidden lg:block"
        }`}
        style={{ background: "var(--noam-sidebar)" }}
        data-testid="saas-sidebar"
      >
        <div className="px-5 py-5 border-b border-white/5 flex items-center gap-2">
          {/* D2 — le logo NOAM ramène au hub 3+1 (hors démo) */}
          {isDemo ? (
            <div className="flex items-center gap-2 flex-1 min-w-0" data-testid="sidebar-logo">
              <div className="w-8 h-8 rounded-md bg-[#378ADD]/20 border border-[#378ADD]/40 flex items-center justify-center shrink-0">
                <span className="noam-display font-bold text-[#378ADD]">N</span>
              </div>
              {!collapsed && (
                <div className="min-w-0">
                  <div className="noam-display font-semibold leading-none">Noam</div>
                  <div className="text-[10px] text-[#B5D4F4]/70 mt-0.5 truncate">{t("saas.shell.system_subtitle")}</div>
                </div>
              )}
            </div>
          ) : (
          <Link to="/app/hub" className="flex items-center gap-2 flex-1 min-w-0" data-testid="sidebar-logo" title="Hub NOAM">
            <div className="w-8 h-8 rounded-md bg-[#378ADD]/20 border border-[#378ADD]/40 flex items-center justify-center shrink-0">
              <span className="noam-display font-bold text-[#378ADD]">N</span>
            </div>
            {!collapsed && (
              <div className="min-w-0">
                <div className="noam-display font-semibold leading-none">Noam</div>
                <div className="text-[10px] text-[#B5D4F4]/70 mt-0.5 truncate">{t("saas.shell.system_subtitle")}</div>
              </div>
            )}
          </Link>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="hidden lg:block text-[#B5D4F4]/60 hover:text-white p-1"
            title={collapsed ? t("saas.shell.expand") : t("saas.shell.collapse")}
            data-testid="sidebar-collapse-btn"
          >
            {collapsed ? <Menu className="w-4 h-4" /> : <X className="w-4 h-4" />}
          </button>
        </div>

        {/* D3 — sidebar contextuelle : DANS un System souscrit, la navigation
            du System remplace la sidebar legacy (shell uniquement, zéro page
            déplacée). Hors System (hub, pages transverses) : sidebar inchangée. */}
        {/* AGENTIC V1 — shell minimal : NOAM orchestre, la machine reste en mode Expert */}
        {agenticMode ? (
          <div data-testid="agentic-sidebar">
            {[
              { to: "/app/noam", label: t("saas.axp3.nav_talk"), icon: Sparkles },
              { to: "/app/aujourdhui", label: t("saas.axp3.nav_today"), icon: Sun },
              { to: "/app/documents", label: t("saas.axp3.nav_docs"), icon: FolderOpen },
              { to: "/app/alertes", label: t("saas.axp3.nav_alerts"), icon: Bell },
              { to: "/app/plans", label: t("saas.axp3.nav_plans"), icon: ListChecks },
            ].map((it) => (
              <NavLink key={it.to} to={remap(it.to)} onClick={() => setMobileOpen(false)}
                data-testid={`agentic-nav-${it.to.replace(/\//g, "-").replace(/^-+/, "")}`}
                className={({ isActive }) => `noam-nav-item mt-1 ${isActive ? "active" : ""} ${collapsed ? "justify-center" : ""}`}>
                <it.icon className="w-4 h-4 opacity-80 shrink-0" />
                {!collapsed && <span className="flex-1">{it.label}</span>}
              </NavLink>
            ))}
            {expMode?.expert_allowed && !collapsed && (
              <button onClick={() => switchExperience("expert")} data-testid="agentic-switch-expert"
                className="mx-4 mt-6 px-3 py-2 rounded-md flex items-center gap-2 text-xs text-[#B5D4F4]/70 border border-white/10 hover:bg-white/5 hover:text-white transition-colors w-[calc(100%-2rem)]">
                <Wrench className="w-3.5 h-3.5" /> {t("saas.axp3.expert_mode")}
              </button>
            )}
          </div>
        ) : systemNavActive ? (
          <SystemSidebar
            system={activeSystem}
            collapsed={collapsed}
            allowedModules={allowedModules}
            onNavigate={() => setMobileOpen(false)}
          />
        ) : (
        <>
        <Link
          to={`${basePath}/configurer`}
          data-testid="sidebar-cta-assistant"
          className={`mx-${collapsed ? "2" : "4"} mt-4 mb-2 px-3 py-2.5 rounded-md flex items-center gap-2 text-sm bg-[#378ADD]/15 border border-[#378ADD]/30 text-white hover:bg-[#378ADD]/25 transition-colors`}
          title={t("saas.shell.assistant_config")}
        >
          <Wand2 className="w-4 h-4 shrink-0" />
          {!collapsed && <><span className="flex-1">{t("saas.shell.assistant_config")}</span><ArrowUpRight className="w-3.5 h-3.5" /></>}
        </Link>

        {/* Vague 20 — Vues intelligentes : switcher de vue */}
        {!collapsed && (
          <div className="mx-4 mt-2 mb-1" data-testid="sidebar-view-switcher">
            <ViewSwitcher />
          </div>
        )}
        {/* AGENTIC V1 — retour au mode agentic depuis le mode Expert */}
        {!collapsed && !isDemo && expMode && (
          <button onClick={() => switchExperience("agentic")} data-testid="expert-switch-agentic"
            className="mx-4 mt-1 mb-1 px-3 py-1.5 rounded-md flex items-center gap-2 text-[11px] text-[#B5D4F4]/60 border border-white/5 hover:bg-white/5 hover:text-white transition-colors w-[calc(100%-2rem)]">
            <Sparkles className="w-3 h-3 text-[#378ADD]" /> Mode Agentic : parler à NOAM
          </button>
        )}

        {SECTIONS.map((sec) => {
          const isOpen = collapsed || openUniverse === sec.label;
          return (
          <div key={sec.label} className="mt-4">
            {!collapsed && (
              <button onClick={() => setOpenUniverse(isOpen ? null : sec.label)} className="w-full px-5 mb-1 text-left group" data-testid={`sidebar-universe-${sec.label.replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`}>
                <div className={`flex items-center gap-2 text-[10px] font-semibold tracking-[0.18em] uppercase transition-colors ${isOpen ? "text-[#378ADD]" : "text-[#B5D4F4]/50 group-hover:text-[#378ADD]"}`}>
                  <sec.icon className="w-3 h-3" />
                  {sec.label}
                  <ChevronDown className={`w-3 h-3 ml-auto transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </div>
                {isOpen && <div className="text-[10px] text-[#B5D4F4]/50 mt-0.5">{sec.sub}</div>}
              </button>
            )}
            {collapsed && <div className="h-px mx-3 my-3 bg-white/5" />}
            {!isOpen ? null : (
            <div>
              {sec.items.map((it, idx) => {
                const displayLabel = isDemo ? it.label : personaLabelFor(it.module, it.label);
                return (
                <NavLink
                  key={`${sec.label}-${it.label}-${idx}`}
                  to={it.to}
                  end={it.to === `${basePath}/dashboard` || it.to === `${basePath}/executive`}
                  data-testid={`sidebar-nav-${it.to.replace(/\//g, "-").replace(/^-+/, "")}-${idx}`}
                  className={({ isActive }) =>
                    `noam-nav-item ${isActive ? "active" : ""} ${collapsed ? "justify-center" : ""}`
                  }
                  title={collapsed ? displayLabel : ""}
                  onClick={() => setMobileOpen(false)}
                >
                  <it.icon className="w-4 h-4 opacity-80 shrink-0" />
                  {!collapsed && (
                    <>
                      <span className="flex-1">{displayLabel}</span>
                      {it.isMetacog && <MetacogBadge />}
                      {/* V23.15.28 — Point discret "Noam a des recommandations non lues" */}
                      {it.module === "intelligence" && aiBriefUnseen && (
                        <span
                          className="w-1.5 h-1.5 rounded-full bg-[#EF9F27] shadow-[0_0_6px_rgba(239,159,39,0.7)] animate-pulse"
                          title="Noam a préparé des recommandations pour toi"
                          data-testid="sidebar-nav-ai-unseen-dot"
                        />
                      )}
                      {/* V23.16 — Badge messages non-lus du Chat interne (orange si @mention) */}
                      {it.module === "chat_interne" && chatUnread.total > 0 && (
                        <span
                          className={`text-[10px] min-w-[18px] h-[18px] px-1 rounded-full font-bold flex items-center justify-center ${chatUnread.mentions > 0 ? "bg-[#EF9F27] text-black" : "bg-[#1D9E75] text-black"}`}
                          title={chatUnread.mentions > 0 ? `${chatUnread.total} non-lu(s) dont ${chatUnread.mentions} @mention(s)` : `${chatUnread.total} message(s) non-lu(s)`}
                          data-testid="sidebar-chat-unread-badge"
                        >
                          {chatUnread.total > 99 ? "99+" : chatUnread.total}
                        </span>
                      )}
                      {it.badge != null && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-[#B5D4F4]/70">
                          {it.badge}
                        </span>
                      )}
                    </>
                  )}
                  {collapsed && it.isMetacog && <MetacogBadgeCollapsed />}
                  {/* V23.15.28 — Version collapsed du dot IA */}
                  {collapsed && it.module === "intelligence" && aiBriefUnseen && (
                    <span
                      className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-[#EF9F27] shadow-[0_0_4px_rgba(239,159,39,0.7)] animate-pulse"
                      title="Noam a préparé des recommandations"
                      data-testid="sidebar-nav-ai-unseen-dot-collapsed"
                    />
                  )}
                  {collapsed && it.module === "chat_interne" && chatUnread.total > 0 && (
                    <span
                      className={`absolute top-1 right-1 w-2 h-2 rounded-full ${chatUnread.mentions > 0 ? "bg-[#EF9F27]" : "bg-[#1D9E75]"}`}
                      title={`${chatUnread.total} message(s) non-lu(s)`}
                      data-testid="sidebar-chat-unread-dot-collapsed"
                    />
                  )}
                </NavLink>
                );
              })}
            </div>
            )}
          </div>
          );
        })}
        </>
        )}

        {!collapsed && (
          <div className="px-5 py-5 border-t border-white/5 mt-6 space-y-3">
            <button
              onClick={() => setWelcomeOpen(true)}
              className="w-full flex items-center gap-2 text-xs text-zinc-500 hover:text-white transition-colors group"
              data-testid="sidebar-why-noam"
            >
              <HelpCircle className="w-3.5 h-3.5 text-[#7F77DD] group-hover:text-white" />
              <span>{t("saas.axp3.why_noam")}</span>
            </button>
            <LanguageSwitcher />
          </div>
        )}
      </aside>

      <div className="flex-1 min-w-0">
        {isDemo && <DemoBanner />}
        {!isDemo && <ImpersonationBanner />}
        <header className="h-14 border-b border-white/5 px-4 lg:px-6 flex items-center justify-between sticky top-0 bg-[var(--noam-bg)]/80 backdrop-blur z-30 gap-2">
          <div className="flex items-center gap-3 text-sm text-zinc-400 min-w-0 flex-1 overflow-hidden">
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden inline-flex items-center justify-center w-11 h-11 -ml-2 rounded-md border border-white/15 bg-white/5 hover:bg-white/10 text-white flex-shrink-0 active:scale-95 transition"
              aria-label="Ouvrir le menu de navigation"
              data-testid="header-mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </button>
            {isDemo ? (
              <span className="hidden md:inline whitespace-nowrap truncate">{t("saas.shell.space")} : <span className="text-white">Banque CIC · Démo</span></span>
            ) : (
              <span className="hidden md:inline-flex items-center gap-2 min-w-0" data-testid="topbar-org">
                <span className="inline-flex items-center gap-1.5 text-[10px] px-2 py-0.5 rounded-full border border-[#1D9E75]/40 bg-[#1D9E75]/10 text-[#1D9E75] uppercase tracking-wider font-semibold whitespace-nowrap flex-shrink-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1D9E75] animate-pulse" />
                  {t("saas.shell.connected")}
                </span>
                <OrgSwitcher activeOrgId={orgId} activeOrgName={orgName} onChanged={onOrgChanged} />
              </span>
            )}
            {/* D2 — sélecteur de System 3+1 (change le contexte de navigation uniquement) */}
            {!isDemo && (
              <>
                <span className="opacity-30 hidden md:inline flex-shrink-0">·</span>
                <SystemSelector />
              </>
            )}
            <span className="opacity-30 hidden md:inline flex-shrink-0">·</span>
            <div className="hidden md:flex flex-shrink-0"><PeriodPicker /></div>
            {(isDemo || phase) && (
              <>
                <span className="opacity-30 hidden md:inline ml-1 flex-shrink-0">·</span>
                <div className="relative hidden md:block flex-shrink-0" data-testid="phase-dropdown" data-topbar-phase>
                  <button
                    onClick={(e) => {
                      if (!phaseOpen) {
                        const r = e.currentTarget.getBoundingClientRect();
                        setPhaseAnchor({ top: Math.round(r.bottom + 6), left: Math.round(r.left) });
                      }
                      setPhaseOpen(!phaseOpen);
                    }}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md border transition-colors whitespace-nowrap ${
                      phaseOpen
                        ? "border-[#378ADD]/60 bg-[#378ADD]/15 text-white"
                        : "border-white/10 bg-white/[0.03] text-zinc-300 hover:border-[#378ADD]/40 hover:bg-[#378ADD]/10 hover:text-white"
                    }`}
                    title={t("settings.phase")}
                    data-testid="phase-dropdown-btn"
                  >
                    <Compass className="w-3 h-3 text-[#378ADD] shrink-0" />
                    <span className="hidden 2xl:inline text-[10px] uppercase tracking-wider text-zinc-500">{t("saas.shell.phase")}</span>
                    <span className="text-[#378ADD] font-medium text-xs">{t(`settings.values.phase.${phase}`, phase)}</span>
                    <ChevronDown className={`w-3 h-3 text-zinc-500 transition-transform ${phaseOpen ? "rotate-180" : ""}`} />
                  </button>
                  {phaseOpen && (
                    <div
                      className="noam-card py-1 min-w-[180px]"
                      style={{ position: "fixed", top: phaseAnchor.top, left: phaseAnchor.left, zIndex: 9999 }}
                      data-testid="phase-dropdown-menu"
                      data-topbar-phase-menu
                    >
                      {PHASES.map((p) => (
                        <button
                          key={p}
                          onClick={() => changePhase(p)}
                          className={`w-full text-left px-3 py-2 text-xs hover:bg-white/5 ${p === phase ? "text-[#378ADD] bg-[#378ADD]/10" : "text-zinc-300"}`}
                          data-testid={`phase-option-${p}`}
                        >
                          {t(`settings.values.phase.${p}`, p)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          <div className="flex items-center gap-1.5 pl-3 ml-2 border-l border-white/5 flex-shrink-0">
            <PersonaSwitcher compact isAdmin={myRole === "admin" || myRole === "super_admin" || isUltraAdmin} />
            <LlmQuotaBadge />
            {/* V23.15.22 — Indicateur visuel de synchronisation de conscience (Doctrine 49 + 12) */}
            <ContextSyncIndicator />
            {!isDemo && <AsyncTasksBadge />}

            {/* V23.14.10 — Tout regroupé dans le menu « Actions » : recherche, contexte, importer, brief comité, configurer, loupe, export, langue, thème */}
            <div className="relative flex-shrink-0" data-topbar-more>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!moreOpen) {
                    const r = e.currentTarget.getBoundingClientRect();
                    setMoreAnchor({ top: Math.round(r.bottom + 6), right: Math.max(8, Math.round(window.innerWidth - r.right)) });
                  }
                  setMoreOpen((v) => !v);
                }}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border transition-colors text-xs ${
                  moreOpen
                    ? "border-[#378ADD]/60 bg-[#378ADD]/15 text-white"
                    : "border-white/10 text-zinc-300 hover:text-white hover:border-[#378ADD]/40 hover:bg-[#378ADD]/10"
                }`}
                title="Actions"
                data-testid="header-more-btn"
              >
                <MoreHorizontal className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{t("common.actions", { defaultValue: "Actions" })}</span>
              </button>
              {moreOpen && (
                <div
                  className="noam-card py-1.5 min-w-[260px]"
                  style={{ position: "fixed", top: moreAnchor.top, right: moreAnchor.right, zIndex: 9999 }}
                  data-testid="header-more-menu"
                  data-topbar-more-menu
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Recherche globale */}
                  <button
                    type="button"
                    onClick={() => {
                      window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }));
                      setMoreOpen(false);
                    }}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs text-zinc-200 hover:bg-white/5 hover:text-white"
                    data-testid="header-more-search"
                  >
                    <span className="flex items-center gap-2">
                      <Search className="w-3.5 h-3.5 text-[#378ADD]" />
                      <span>{t("common.search", { defaultValue: "Rechercher" })}</span>
                    </span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 text-zinc-400">⌘K</span>
                  </button>

                  {/* Contexte module */}
                  {globalContextModuleKey && (
                    <div className="px-3 py-2 hover:bg-white/5">
                      <ContextButton moduleKey={globalContextModuleKey} fullWidth />
                    </div>
                  )}

                  <div className="h-px bg-white/5 my-1" />

                  {/* Configurer */}
                  <Link
                    to={`${basePath}/configurer`}
                    onClick={() => setMoreOpen(false)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-200 hover:bg-white/5 hover:text-white"
                    data-testid="header-more-configure"
                  >
                    <Settings className="w-3.5 h-3.5 text-[#378ADD]" />
                    <span>{t("common.configure")}</span>
                  </Link>

                  {/* V23.15.10 — Agents IA (workspace admin) */}
                  {!isDemo && (["admin", "super_admin"].includes(myRole) || isUltraAdmin) && (
                    <Link
                      to={`${basePath}/settings/agents`}
                      onClick={() => setMoreOpen(false)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-200 hover:bg-white/5 hover:text-white"
                      data-testid="header-more-agents"
                    >
                      <Sparkles className="w-3.5 h-3.5 text-[#EF9F27]" />
                      <span>{t("saas.nav.agents", { defaultValue: "Agents IA" })}</span>
                    </Link>
                  )}

                  {/* V23.15.25 — Calibration Multipliers Brute/Net (workspace admin) */}
                  {!isDemo && (["admin", "super_admin"].includes(myRole) || isUltraAdmin) && (
                    <Link
                      to={`${basePath}/settings/multipliers`}
                      onClick={() => setMoreOpen(false)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-200 hover:bg-white/5 hover:text-white"
                      data-testid="header-more-multipliers"
                    >
                      <Sliders className="w-3.5 h-3.5 text-[#378ADD]" />
                      <span>Calibration Brute/Net</span>
                    </Link>
                  )}

                  {/* Importer */}
                  {!isDemo && (["admin", "super_admin", "contributor", "contributeur"].includes(myRole) || isUltraAdmin) && (
                    <button
                      type="button"
                      onClick={() => { setImportOpen(true); setMoreOpen(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-200 hover:bg-white/5 hover:text-white"
                      data-testid="header-more-import"
                    >
                      <UploadCloud className="w-3.5 h-3.5 text-[#7F77DD]" />
                      <span>{t("saas.import.button_short", { defaultValue: "Importer" })}</span>
                    </button>
                  )}

                  {/* Brief comité */}
                  {!isDemo && (["admin", "super_admin", "contributor", "contributeur"].includes(myRole) || isUltraAdmin) && (
                    <button
                      type="button"
                      onClick={() => { setBriefOpen(true); setMoreOpen(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-200 hover:bg-white/5 hover:text-white"
                      data-testid="header-more-brief"
                    >
                      <ClipboardList className="w-3.5 h-3.5 text-[#EF9F27]" />
                      <span>{t("saas.brief.button", { defaultValue: "Brief de comité" })}</span>
                    </button>
                  )}

                  <div className="h-px bg-white/5 my-1" />

                  {!isDemo && (
                    <button
                      type="button"
                      onClick={() => { setLensOpen(true); setMoreOpen(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-200 hover:bg-white/5 hover:text-white"
                      data-testid="header-more-ext-lens"
                    >
                      <Globe className="w-3.5 h-3.5 text-[#378ADD]" />
                      <span>Loupe externe</span>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setMoreOpen(false)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-200 hover:bg-white/5 hover:text-white"
                    data-testid="header-more-export"
                  >
                    <Download className="w-3.5 h-3.5 text-zinc-400" />
                    <span>{t("common.export")}</span>
                  </button>

                  <div className="h-px bg-white/5 my-1" />

                  <div className="px-3 py-2 flex items-center justify-between gap-3">
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500">Langue</span>
                    <LanguageSwitcher compact />
                  </div>
                  <div className="px-3 py-2 flex items-center justify-between gap-3">
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500">Thème</span>
                    <ThemeToggle compact />
                  </div>
                </div>
              )}
            </div>

            {/* Sélecteur de langue visible en permanence (demande user : évident pour les testeurs) */}
            <div className="hidden sm:block" data-testid="header-language-switcher">
              <LanguageSwitcher compact />
            </div>
            {isDemo ? <DemoUserMenu /> : <UserMenu />}
          </div>
        </header>
        <IntroductionBanner />
        <div className={`p-4 lg:p-6 min-w-0 overflow-x-hidden ${agenticMode ? "pb-32 lg:pb-32" : ""}`}>
          <Outlet key={`period-${periodEpoch}`} />
        </div>
        {/* AGENTIC : fenêtre conversationnelle persistante, écrire ou parler à NOAM depuis partout */}
        {agenticMode && !["/app/noam", "/app/intelligence"].includes(location.pathname) && (
          <AgenticGlobalBar navigate={navigate} />
        )}
      </div>
      <ExternalLensModal open={lensOpen} onClose={() => setLensOpen(false)} />
      {/* V23.14.10 — Modals déclenchées depuis le menu Actions, montées au niveau layout */}
      {importOpen && (
        <ImportIntelligentModal
          onClose={() => setImportOpen(false)}
          onApplied={() => setTimeout(() => window.location.reload(), 600)}
        />
      )}
      {briefOpen && <BriefComiteModalRoot onClose={() => setBriefOpen(false)} />}
      {/* V23.11 — Onboarding guidé : modal de choix 1re connexion + checklist sticky */}
      <OnboardingFlow />
      {/* V23.15.44 — Welcome modal painkillers (first login) + réouvrable */}
      {!isDemo && <WelcomeModal forceOpen={welcomeOpen} onClose={() => setWelcomeOpen(false)} />}
    </div>
    </MetacogUnreadProvider>
  );
}

// V22 — Badges Métacognition (consomment le contexte MetacogUnreadProvider)
function MetacogBadge() {
  const { total } = useMetacogUnread();
  if (!total) return null;
  return (
    <span
      className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-[#E24B4A] text-white"
      data-testid="metacog-sidebar-badge"
    >
      {total}
    </span>
  );
}

function MetacogBadgeCollapsed() {
  const { total } = useMetacogUnread();
  if (!total) return null;
  return (
    <span
      className="absolute top-1 right-1 w-2 h-2 rounded-full bg-[#E24B4A]"
      data-testid="metacog-sidebar-badge-dot"
    />
  );
}
